import Navigation from "@/components/site/Navigation";
import Contact from "@/components/site/Contact";
import Footer from "@/components/site/Footer";
import LiveStatus from "@/components/site/LiveStatus";
import { MapPin, Phone, Mail, Clock, Building2 } from "lucide-react";

const OFFICES = [
  {
    icon: Building2,
    label: "Headquarters",
    name: "Yiwu, China",
    body: "402, Wealth Mansion, Yiwu, Zhejiang, China · 322000",
    accent: true,
  },
  {
    icon: MapPin,
    label: "Branch",
    name: "Guangzhou",
    body: "South-China sourcing & freight coordination office.",
  },
  {
    icon: MapPin,
    label: "Partner Office",
    name: "United Kingdom",
    body: "Customs handling and last-mile coordination across the UK.",
  },
  {
    icon: MapPin,
    label: "Partner Office",
    name: "India",
    body: "Door-to-door cargo, customs clearance and on-ground client support.",
  },
];

const MARKETS = [
  "Bahrain", "Bangladesh", "Canada", "India", "Kuwait", "Oman",
  "Qatar", "Saudi Arabia", "Spain", "UAE", "United Kingdom", "United States",
];

export default function ContactPage() {
  return (
    <main data-testid="contact-page" className="bg-ink-50">
      <Navigation />

      <section
        data-testid="contact-page-hero"
        className="relative pt-36 md:pt-44 pb-16 md:pb-20 bg-white border-b border-ink-100"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
            Contact
          </p>
          <h1 className="font-heading font-black text-ink-900 tracking-[-0.035em] leading-[0.92] text-5xl md:text-6xl lg:text-7xl max-w-4xl">
            One inbox.<br />
            <span className="text-stroke">Four offices.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-ink-500 text-base md:text-lg leading-relaxed">
            Yiwu HQ, Guangzhou branch, partner offices in the UK and India —
            shipping into 15+ countries. Pick a method below or use the form.
          </p>
          <div className="mt-8">
            <LiveStatus variant="full" />
          </div>
        </div>
      </section>

      {/* Quick contact strip */}
      <section className="bg-ink-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
            <a
              href="tel:008618367978727"
              data-testid="contact-quick-phone"
              className="group p-7 bg-white border border-ink-100 hover:border-signal transition-colors"
            >
              <Phone className="w-5 h-5 text-signal" strokeWidth={1.6} />
              <div className="mt-5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink-500">Call us</div>
              <div className="mt-1 font-heading font-bold text-xl text-ink-900 group-hover:text-signal transition-colors">
                +86 183 6797 8727
              </div>
            </a>
            <a
              href="mailto:info@emporiaworld.co"
              data-testid="contact-quick-email"
              className="group p-7 bg-white border border-ink-100 hover:border-signal transition-colors"
            >
              <Mail className="w-5 h-5 text-signal" strokeWidth={1.6} />
              <div className="mt-5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink-500">Email</div>
              <div className="mt-1 font-heading font-bold text-xl text-ink-900 group-hover:text-signal transition-colors">
                info@emporiaworld.co
              </div>
            </a>
            <div
              data-testid="contact-quick-hours"
              className="p-7 bg-white border border-ink-100"
            >
              <Clock className="w-5 h-5 text-signal" strokeWidth={1.6} />
              <div className="mt-5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink-500">Hours (CST)</div>
              <div className="mt-1 font-heading font-bold text-xl text-ink-900">
                Mon–Sat · 09:00 – 17:00
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Offices */}
      <section className="bg-white py-14 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end mb-12 md:mb-16">
            <div className="md:col-span-7">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
                Offices
              </p>
              <h2 className="font-heading font-black text-ink-900 tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl">
                Closer than you think.
              </h2>
            </div>
            <p className="md:col-span-5 text-ink-500 text-base leading-relaxed">
              Whether you ship into Mumbai, Manchester or Manama — there's an
              Emporia operator within reach.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {OFFICES.map((o) => {
              const Icon = o.icon;
              return (
                <div
                  key={o.name}
                  data-testid={`office-${o.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`p-7 md:p-8 border transition-colors ${
                    o.accent
                      ? "bg-ink-900 text-white border-ink-900"
                      : "bg-ink-50 border-ink-100 hover:border-signal"
                  }`}
                >
                  <Icon className={`w-5 h-5 ${o.accent ? "text-signal" : "text-ink-900"}`} strokeWidth={1.6} />
                  <div
                    className={`mt-5 font-mono text-[11px] uppercase tracking-[0.22em] ${
                      o.accent ? "text-signal" : "text-ink-500"
                    }`}
                  >
                    {o.label}
                  </div>
                  <h3
                    className={`mt-2 font-heading font-bold text-2xl tracking-tight ${
                      o.accent ? "text-white" : "text-ink-900"
                    }`}
                  >
                    {o.name}
                  </h3>
                  <p
                    className={`mt-3 text-[14px] leading-relaxed ${
                      o.accent ? "text-white/70" : "text-ink-500"
                    }`}
                  >
                    {o.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Markets served */}
      <section className="bg-ink-50 py-20 md:py-24 border-y border-ink-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-4">
                Markets served
              </p>
              <h2 className="font-heading font-black text-ink-900 tracking-[-0.02em] leading-[1.05] text-3xl md:text-4xl">
                Shipping into 15+ countries.
              </h2>
            </div>
          </div>
          <ul
            data-testid="markets-list"
            className="flex flex-wrap gap-2.5 md:gap-3"
          >
            {MARKETS.map((m) => (
              <li
                key={m}
                className="px-4 py-2 bg-white border border-ink-100 hover:border-signal hover:text-signal transition-colors font-mono text-xs uppercase tracking-[0.18em] text-ink-800"
              >
                {m}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Reuse the home Contact section (form + newsletter) */}
      <Contact />

      <Footer />
    </main>
  );
}
